export type AssessmentScore = {
  aSigma: number;
  bSigma: number;
  cSigma: number;
  dSigma: number;
  eSigma: number;
};
export interface BaPersonInfo {
  name: PersonName;
  assessmentUserId: string;
  assessmentId: string;
  selfAssessmentScore?: AssessmentScore;
  referencePattern?: ReferencePattern;
  preferredWorkStyles?: string[];
  personNeedsListContent?: PersonNeeds[];
  strengths?: string;
  cautionAreas?: string;
  factors?: Factor[];
  factorCombinations?: FactorCombinations[];
  factorDescriptions?: FactorDescriptions[];
  hasPendingBa: boolean;
}
export interface PersonName {
  first: string;
  last: string;
  middle: string;
  goesBy: string;
  prefix: string;
  suffix: string;
}
export type Factor = {
  letter: string;
  sigmaZone: number;
  absoluteSigmaValue: number;
  motivatingNeedTrait: string;
  behaviorTraits: string;
  sigmaRangeTraits: string;
  rightFactorTrait: string;
  factorNameKey: string;
  primaryTrait: string;
};
export type FactorCombinations = {
  traitsKey: string;
  type: number;
  midpointRelationship: number;
  factorDistance: number;
  factorDifference: number;
  typeDescriptionKey: string;
  primaryTraitKey: string;
  typeDescription: string;
};
export type FactorDescriptions = {
  factorLetterKey: string;
  factorDescriptionKey: string;
  factorFullDescriptionKey: string;
};
export type PersonNeeds = {
  needsListContentTitle: string;
  patternInsightsNeeds: string;
  needsListContent: string;
};
export type ReferencePattern = {
  patternNumber: number;
  referencePatternName: ReferencePatternName;
  singularName: string;
  pluralName: string;
  description: string;
  descriptionBolded: string;
};

export enum ReferencePatternName {
  adapter = 'Adapter',
  altruist = 'Altruist',
  analyzer = 'Analyzer',
  artisan = 'Artisan',
  captain = 'Captain',
  collaborator = 'Collaborator',
  controller = 'Controller',
  craftsman = 'Craftsman',
  guardian = 'Guardian',
  individualist = 'Individualist',
  maverick = 'Maverick',
  operator = 'Operator',
  persuader = 'Persuader',
  promoter = 'Promoter',
  scholar = 'Scholar',
  specialist = 'Specialist',
  strategist = 'Strategist',
  venturer = 'Venturer',
}

export enum BaInfoStatus {
  BA_FETCHING,
  BA_NOT_FOUND,
  BA_FOUND,
  BA_PENDING_PROFILE,
}

export type PersonServerPost = {
  firstName: string;
  middleName: string;
  lastName: string;
  goesByName?: string;
  gender?: string;
  email: string;
  folderPath?: string;
  folderId?: string;
  externalPersonId?: string;
  associatedJobTitle?: string;
  associatedJobId?: string;
  personType: string;
};

export type PersonServerResponse = {
  firstName: string;
  middleName: string;
  lastName: string;
  goesByName: string;
  gender: string;
  email: string;
  folderId: string;
  folderPath: string;
  externalPersonId: string;
  associatedJobTitle: string;
  associatedCompanyName: string;
  impression: number;
  isArchived: boolean;
  personId: string;
  personType: keyof typeof SurveyUserTypes;
};

export enum SurveyUserTypes {
  Unknown = 1,
  Other = 2,
  Candidate = 3,
  Employee = 4,
  FormerEmployee = 5,
}

export type PersonUpdate = {
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  folder: Folder;
  type: SurveyUserTypes;
  job?: Job | 'RemoveJob';
};

export type Folder = {
  readonly id: string;
  readonly name: string;
  readonly childrenIds: string[];
  readonly children: Folder[];
  readonly parentId: string;
  readonly folderPath?: string;
};

export type Job = {
  readonly id: string;
  readonly title: string;
  readonly behavioralRanges?: BehavioralFactorRanges;
  readonly folder?: Folder;
  readonly jobPipeline: JobPipelineHealth;
  readonly createdDate?: Date;
  readonly lastUpdated: Date;
};

export type JobPipelineHealth = {
  healthCounts: JobPipelineHealthCounts;
  totalCandidates: number;
  jobId: string;
  applicantIds: string[];
};

export type JobPipelineHealthCounts = { [V in JobMatchValue]: number };

export type BehavioralFactorRanges = {
  [key: string]: MinMax;
  A: MinMax;
  B: MinMax;
  C: MinMax;
  D: MinMax;
};

export type MinMax = {
  min: number;
  max: number;
};

export enum JobMatchValue {
  Pending,
  Low,
  Ok,
  Great,
}

export enum InvitationSource {
  Unknown = 0,
  AssessmentCenter,
  Design,
  SelfService,
  Hire,
  Design2,
  Inspire2,
  Admin,
  Perform,
}

export type OnlySuccessSuccess = {
  tag: number;
  value: any;
  status: string;
};

export class ScheduledAssessmentDetails {
  assessments: AssessmentDetail[];
  cognitiveEmail: EmailDetails;
  behavioralEmail: EmailDetails;
  expirationDateTime: Date;
  behavioralScoreId: string;
  assessmentDateForScoreId: string;
  sendBehavioral = true;
  sendCognitive = false;
  sendCognitiveAfterBehavioralIsComplete = false;
  resendBehavioralAssessmentId: string;
}

export class AssessmentDetail {
  person: Person;
  job: Job;
  autoSendBehavioralReport: boolean;
}

export class Person {
  readonly gender: string;
  readonly userId: string;
  readonly folder: Folder;
  id: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;

  // lastCompleted is a little unfortunate since it is technically duplicated
  // data, but we get an abbreviated set of data for table display and want
  // this data shown despite not having full info on assessments
  readonly lastCompleted: Date;
  readonly lastSent: Date;
  readonly lastUpdated: Date;

  job: Job;
  // Default job access to true unless we've checked
  hasJobAccess = true;
  isManager: boolean;
}

export class Language {
  display = 'English'; // defaults
  locale = 'en-US'; // defaults
  drupalCode = 'en';

  constructor(obj?: Partial<Language>) {
    Object.assign(this, obj);
  }
}

export class EmailDetails {
  subject = '';
  message = '';
  // this is technically what was last loaded and not an analysis of text
  languageOfSubject: string;
  languageOfMessage: string;
  language: Language;
  administrator: any;
  assessmentExpirationDate: Date;
  type: GlobalEmailTypes;
  sendAnalystCompletionEmail: boolean;
}

// This is to be synched with Server
export enum GlobalEmailTypes {
  BehavioralAssessmentEmail = 1,
  JobAssessmentEmail = 2,
  BehavioralAssessmentResultsEmail = 3,
  JobAssessmentResultEmail = 4,
  InterviewGuide = 5,
  CoachingGuide = 6,
  BehavioralReport = 7,
  JobReport = 8,
  PatternPlacard = 9,
  PersonalDevelopmentChart = 10,
  CognitiveAssessmentEmail = 11,
  ManagerDevelopmentChart = 12,
  ManagementStrategyGuide = 13,
  StrategyAssessmentEmail = 14,
  JobTargetReport = 15,
}

export const InvalidAssessmentId = '00000000-0000-0000-0000-000000000000';
